import React, { CSSProperties, ReactElement, ReactNode } from 'react';
import TypelessLabelBlock from '../../components/connectedForm/LabelBlock';
import Label from '../../components/connectedForm/Label';
import RequiredIcon from '../../components/connectedForm/RequiredIcon';
import HintIcon from './HintIcon';
import ValidationHintIcon from './ValidationHintIcon';
import { Maybe } from '../../graphql/schema/p4types';
import { makeStyles, Tooltip } from '@fluentui/react-components';
import styled from 'styled-components';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { TriggerBlock } from './TriggerPopover/TriggerPopover';

//Temporary measure.
const LabelBlock: any = TypelessLabelBlock;

const useStyles = makeStyles({
  content: {
    maxWidth: '360px',
  },
});

//Shrink the inner div to the content so the tooltip appears over the label, not the whitespace between the label and field.
const TooltipDiv = styled.div`
  width: max-content;
  max-width: 100%;
`;

export type IInputLabelProps = {
  alignLabel?: string;
  flexBox?: boolean;
  hint?: ReactNode;
  id?: string;
  includeLabel?: boolean;
  label?: Maybe<string>;
  cols?: string | number;
  mandatory?: boolean;
  readOnly?: boolean;
  styleName?: string;
  bold?: boolean;
  reformatLabel?: boolean;
  styles?: { label?: CSSProperties; root?: CSSProperties };
  labelAddon?: ReactNode | ReactElement;
  view?: boolean;
  validationHint?: string;
  uppercaseLabel?: string;
};

const InputLabel = (props: IInputLabelProps) => {
  const {
    alignLabel,
    bold,
    flexBox,
    hint,
    id,
    includeLabel,
    label,
    labelAddon,
    cols,
    mandatory,
    readOnly,
    styleName,
    view,
    validationHint,
    uppercaseLabel,
    reformatLabel,
    styles,
  } = props;

  const tooltipStyles = useStyles();

  if (!includeLabel) {
    return null;
  }

  const labelText = label && reformatLabel ? label.charAt(0).toUpperCase() + label.slice(1).toLowerCase() : label;

  //Tooltips may now be provided as React Elements (or any ReactNode)
  if ((hint || validationHint) && (!view || (hint && typeof hint !== 'string'))) {
    let hintMessage: ReactNode | string;
    if (hint && !(typeof hint === 'string')) {
      //JSX Component for hint, can't simply merge the hint/validationHint as below, may need to be reimplemented eventually.
      hintMessage = hint;
    } else if (hint) {
      hintMessage = validationHint ? `${hint}, ${validationHint}` : hint;
    } else {
      hintMessage = validationHint;
    }

    //'TriggerBlock' is what actually handles the column width and indent, not the label, so we need it even
    //for the new Tooltip (May be worth merging it into 'Label' in a future update).
    return (
      <TriggerBlock cols={cols} flexBox={flexBox}>
        <Label htmlFor={id} bold={bold} style={styles?.label}>
          <Tooltip
            relationship={'label'}
            withArrow={true}
            content={{ className: tooltipStyles.content, children: hintMessage }}
          >
            <TooltipDiv>
              {labelText}
              <HintIcon hint={hint} />
              <ValidationHintIcon hint={validationHint} />
              <RequiredIcon mandatory={mandatory} readOnly={readOnly} />
              {labelAddon}
            </TooltipDiv>
          </Tooltip>
        </Label>
      </TriggerBlock>
    );
  }

  const columns = view ? 1 : cols;
  return (
    <LabelBlock
      cols={columns}
      flexBox={flexBox}
      view={view}
      alignLabel={alignLabel}
      uppercaseLabel={uppercaseLabel}
      style={styles?.root}
    >
      <Label htmlFor={id} view={view} bold={bold} style={styles?.label}>
        {labelText}
        <RequiredIcon mandatory={mandatory} readOnly={readOnly} />
        {labelAddon}
      </Label>
    </LabelBlock>
  );
};

InputLabel.defaultProps = {
  flexBox: true,
  hint: '',
  id: '',
  includeLabel: true,
  label: '',
  cols: 1 / 4,
  mandatory: false,
  readOnly: false,
  styleName: 'onLabelCol',
  bold: false,
  labelAddon: null,
  reformatLabel: true,
};

export default InputLabel;
